import { ReactNode } from 'react';
import { Label, makeStyles, mergeClasses, Text, tokens } from '@fluentui/react-components';
import {
    CalendarRegular,
    ClipboardTaskListLtrRegular,
    FlagRegular,
} from '@fluentui/react-icons';
import { Column, Row } from '../../../common';
import { useCommonStyles } from '../../../common/Styles/commonStyles';
import { useUtilities } from '../../../../utilities/useUtilities';
import { useCaseMilestoneState } from '../../../../redux/reducers/caseMilestoneReducer';
import { useCaseTaskState } from '../../../../redux/reducers/caseTaskReducer';
import { useCaseEventState } from '../../../../redux/reducers/caseEventReducer';
import { CaseDocument } from '../../../../data/types';

const useStyles = makeStyles({
    linkRow: {
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        paddingBlockEnd: '.5rem',
    },
    icon: {
        color: tokens.colorBrandForeground1,
    },
});

type DocumentLinksPanelProps = {
    selectedDoc?: CaseDocument;
};

type LinkItemProps = {
    icon: ReactNode;
    label: string;
    title?: string;
    detail?: string;
    testId: string;
};

export const DocumentLinksPanel = (props: DocumentLinksPanelProps) => {
    const { selectedDoc } = props;

    //Hooks
    const { milestones } = useCaseMilestoneState();
    const { tasks } = useCaseTaskState();
    const { events } = useCaseEventState();
    const utils = useUtilities();

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const subtleClasses = mergeClasses(commonStyles.text.subtleText);

    //Variables
    const dateFormat: Intl.DateTimeFormatOptions = {
        month: 'numeric',
        day: '2-digit',
        year: 'numeric',
    };

    const task = tasks.find((t) => t.taskId === selectedDoc?.taskId);
    const event = events.find((e) => e.eventId === selectedDoc?.eventId);

    // The document may link a milestone directly, or inherit one from its linked task/event.
    const milestoneId = selectedDoc?.milestoneId || task?.milestoneId || event?.milestoneId;
    const milestone = milestones.find((m) => m.milestoneId === milestoneId);
    const milestoneSource = !selectedDoc?.milestoneId
        ? task?.milestoneId === milestoneId
            ? 'task'
            : 'event'
        : undefined;

    const hasLinks = !!milestone || !!task || !!event;

    const LinkItem = (linkProps: LinkItemProps) => {
        const { icon, label, title, detail, testId } = linkProps;

        return (
            <Row className={styles.linkRow} gap='medium' noWrap data-testid={testId}>
                <Column width='auto' vAlign='middle' className={styles.icon}>
                    {icon}
                </Column>
                <Column noGap>
                    <Label className={subtleClasses} size='small'>
                        {label}
                    </Label>
                    <Text>{title}</Text>
                    {!!detail && (
                        <Text className={subtleClasses} size={200}>
                            {detail}
                        </Text>
                    )}
                </Column>
            </Row>
        );
    };

    return (
        <Column gap='medium' data-testid='document-links-panel'>
            <Label size='large'>Links</Label>
            {!hasLinks && (
                <Text className={subtleClasses} italic size={300}>
                    This document is not attached to a milestone, task, or event.
                </Text>
            )}
            {!!milestone && (
                <LinkItem
                    icon={<FlagRegular />}
                    label={milestoneSource ? `Milestone (via ${milestoneSource})` : 'Milestone'}
                    title={milestone.name}
                    detail={
                        milestone.dueDate
                            ? `Due ${utils.date.formatDate(milestone.dueDate, dateFormat, true)}`
                            : undefined
                    }
                    testId='document-links-panel-milestone'
                />
            )}
            {!!task && (
                <LinkItem
                    icon={<ClipboardTaskListLtrRegular />}
                    label='Task'
                    title={task.title}
                    detail={
                        task.dueBy
                            ? `${task.status || ''} \u2022 Due ${utils.date.formatDate(task.dueBy, dateFormat, true)}`
                            : task.status
                    }
                    testId='document-links-panel-task'
                />
            )}
            {!!event && (
                <LinkItem
                    icon={<CalendarRegular />}
                    label='Event'
                    title={event.event}
                    detail={utils.date.formatDate(
                        event.startDateTime || event.date,
                        dateFormat,
                        event.allDay,
                    )}
                    testId='document-links-panel-event'
                />
            )}
        </Column>
    );
};
